import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePostHog } from '@posthog/react'
import ContentHeader from '../components/ContentHeader'
import { FiUpload, FiPackage } from 'react-icons/fi'
import Loader from '../components/Loading'
import { getCrmServiceUrl } from '../utils/backend'
import { getSessionAuth, getAuthHeaders } from '../utils/sessionAuth'
import { extractCatalog, getCatalogStatus, type CatalogFileRecord } from '../api/catalog'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import '../style/views/CatalogUploaderView.css'

const PROCESSED_CATALOG_TITLE = 'catalog'
const DATA_BUFFER_LIMIT_ERROR = 'DataBufferLimitException'
const CONNECTION_REFUSED_ERROR = 'Connection refused'
const CATALOG_STATUS_POLL_INTERVAL_MS = 2000

const getCatalogProgress = (status: string | null | undefined) => {
  const parsed = Number(status ?? 0)
  if (Number.isNaN(parsed)) return 0
  return Math.min(100, Math.max(0, parsed))
}

const hasNumericCatalogProgress = (status: string | null | undefined) => {
  if (!status?.trim()) return false
  return !Number.isNaN(Number(status))
}

const formatCatalogStatus = (status: string | null | undefined) => {
  const normalized = status?.trim()
  if (!normalized) return 'Processing'
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const isDataBufferLimitError = (value: string | null | undefined) =>
  Boolean(value && value.includes(DATA_BUFFER_LIMIT_ERROR))

const isConnectionRefusedError = (value: string | null | undefined) =>
  Boolean(value && value.includes(CONNECTION_REFUSED_ERROR))

const isCatalogDone = (record: CatalogFileRecord | null | undefined) =>
  record?.ifDone === true || record?.status === '100' || record?.status === 'failed'

const isCatalogFailed = (record: CatalogFileRecord | null | undefined) =>
  Boolean(
    record?.status === 'failed' ||
      (isCatalogDone(record) && record?.status_message && /error|failed|critical/i.test(record.status_message)),
  )

function CatalogUploaderView() {
  const posthog = usePostHog()
  const [isUploading, setIsUploading] = useState(false)
  const [isStatusPolling, setIsStatusPolling] = useState(false)
  const [hasUploadAttempted, setHasUploadAttempted] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [catalogRecord, setCatalogRecord] = useState<CatalogFileRecord | null>(null)
  const [rawCatalogMetadata, setRawCatalogMetadata] = useState<CatalogFileRecord | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)
  const [activeUserId, setActiveUserId] = useState<string | null>(null)
  const [isOpeningRawCatalog, setIsOpeningRawCatalog] = useState(false)
  const [rawMetadataRefreshTick, setRawMetadataRefreshTick] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const pollTimerRef = useRef<number | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  const handleDropzoneDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDropzoneDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const droppedFile = e.dataTransfer.files?.[0]
    if (droppedFile) {
      setFile(droppedFile)
      setStatusError(null)
    }
  }

  const stopCatalogPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
    setIsStatusPolling(false)
  }, [])

  const startCatalogPolling = useCallback((userId: string) => {
    setActiveUserId(userId)
    setIsStatusPolling(true)
  }, [])

  const handleBufferLimitFailure = useCallback((message: string) => {
    stopCatalogPolling()
    setCatalogRecord(null)
    setRawMetadataRefreshTick((current) => current + 1)
    setStatusError(
      hasUploadAttempted
        ? 'The uploaded catalog is too large for the current extraction response. You can review the raw PDF and try again.'
        : null,
    )
    if (hasUploadAttempted && message) {
      setStatusError(message)
    }
  }, [hasUploadAttempted, stopCatalogPolling])

  const resetToBaseCatalogView = useCallback(() => {
    setIsUploading(false)
    stopCatalogPolling()
    setCatalogRecord(null)
    setStatusError(null)
  }, [stopCatalogPolling])

  useEffect(() => {
    let cancelled = false

    const restoreCatalogStream = async () => {
      const existingUserId = getSessionUserId()
      if (existingUserId) {
        if (!cancelled) setActiveUserId(existingUserId)
        return
      }

      const ensuredUserId = await ensureSessionUserId(getSessionAuth())
      if (!cancelled && ensuredUserId) {
        setActiveUserId(ensuredUserId)
      }
    }

    restoreCatalogStream()

    return () => {
      cancelled = true
    }
  }, [])

  const parsedSummary = useMemo(() => {
    if (!isCatalogDone(catalogRecord) || !catalogRecord?.content) {
      return null
    }

    try {
      const parsed = JSON.parse(catalogRecord.content) as unknown
      if (Array.isArray(parsed)) {
        return `Extraction completed with ${parsed.length} items.`
      }
      if (parsed && typeof parsed === 'object') {
        const products = (parsed as { products?: unknown }).products
        if (Array.isArray(products)) {
          return `Extraction completed with ${products.length} products.`
        }
      }
      return 'Extraction completed and returned valid JSON.'
    } catch {
      return 'Extraction completed, but the returned content could not be parsed as JSON.'
    }
  }, [catalogRecord])

  useEffect(() => {
    if (!activeUserId) return

    let cancelled = false

    const loadRawCatalogMetadata = async () => {
      try {
        const resp = await fetch(
          `${getCrmServiceUrl()}/catalog/${encodeURIComponent(activeUserId)}/raw/metadata`,
          {
            headers: getAuthHeaders(),
            credentials: 'include',
          },
        )
        if (!resp.ok) {
          if (resp.status === 404 && !cancelled) {
            setRawCatalogMetadata(null)
          }
          return
        }
        const payload = (await resp.json()) as CatalogFileRecord
        if (!cancelled) {
          setRawCatalogMetadata(payload)
        }
      } catch (err) {
        console.error('Failed to load raw catalog metadata', err)
      }
    }

    loadRawCatalogMetadata()

    return () => {
      cancelled = true
    }
  }, [activeUserId, rawMetadataRefreshTick])

  useEffect(() => {
    if (!activeUserId || !isStatusPolling) return

    let cancelled = false
    const scheduleNextPoll = () => {
      if (!cancelled) {
        pollTimerRef.current = window.setTimeout(pollStatus, CATALOG_STATUS_POLL_INTERVAL_MS)
      }
    }

    const pollStatus = async () => {
      try {
        const payload = await getCatalogStatus(activeUserId)
        if (cancelled) return
        const payloadTitle = (payload.title ?? '').trim().toLowerCase()
        if (payloadTitle && payloadTitle !== PROCESSED_CATALOG_TITLE && payloadTitle !== 'raw catalog') {
          scheduleNextPoll()
          return
        }
        if (isConnectionRefusedError(payload.status_message)) {
          resetToBaseCatalogView()
          return
        }
        if (isDataBufferLimitError(payload.status_message)) {
          handleBufferLimitFailure(payload.status_message || '')
          return
        }
        setCatalogRecord(payload)
        setStatusError(null)
        if (isCatalogDone(payload)) {
          const hasFailed = isCatalogFailed(payload)
          posthog?.capture('catalog_extraction_completed', {
            success: !hasFailed,
            status_message: payload.status_message ?? null,
          })
          stopCatalogPolling()
          setRawMetadataRefreshTick((current) => current + 1)
          return
        }
      } catch (err) {
        if (cancelled) return
        console.error('Failed to load catalog status', err)
        setStatusError('Catalog progress update failed. Retrying...')
      }

      scheduleNextPoll()
    }

    pollStatus()

    return () => {
      cancelled = true
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
    }
  }, [activeUserId, handleBufferLimitFailure, isStatusPolling, posthog, resetToBaseCatalogView, stopCatalogPolling])

  const handleConnect = async () => {
    if (!file) {
      setStatusError('Select a PDF catalog before connecting.')
      return
    }

    if (file.type && file.type !== 'application/pdf') {
      setStatusError('Only PDF catalogs are supported for extraction right now.')
      return
    }

    posthog?.capture('catalog_upload_started', {
      file_name: file.name,
      file_size_bytes: file.size,
      file_type: file.type,
    })
    setIsUploading(true)
    setHasUploadAttempted(true)
    setStatusError(null)
    setCatalogRecord({
      title: PROCESSED_CATALOG_TITLE,
      status: '0',
      status_message: 'Uploading catalog and starting extraction...',
    })

    try {
      const userId = await ensureSessionUserId(getSessionAuth())
      if (!userId) {
        throw new Error('Unable to resolve the current user. Please sign in again and retry.')
      }

      let payload: CatalogFileRecord | null = null
      try {
        payload = await extractCatalog(userId, file) as CatalogFileRecord
      } catch (err) {
        const msg = err instanceof Error ? err.message : ''
        throw new Error(msg || 'Unable to start catalog extraction.', { cause: err })
      }

      if (isDataBufferLimitError(payload?.status_message)) {
        handleBufferLimitFailure(payload?.status_message || '')
        return
      }

      setRawMetadataRefreshTick((current) => current + 1)
      setCatalogRecord(payload)
      if (!isCatalogDone(payload)) {
        startCatalogPolling(userId)
      }
    } catch (err) {
      console.error('Failed to start catalog extraction', err)
      const message = err instanceof Error ? err.message : 'Unable to start catalog extraction.'
      if (isConnectionRefusedError(message)) {
        resetToBaseCatalogView()
      } else {
        setStatusError(message)
      }
      setActiveUserId(null)
    } finally {
      setIsUploading(false)
    }
  }

  const handleOpenRawCatalog = async () => {
    const rawWindow = window.open('', '_blank')
    if (rawWindow) {
      rawWindow.opener = null
      rawWindow.document.title = 'Opening raw catalog...'
    }

    setIsOpeningRawCatalog(true)
    setStatusError(null)

    try {
      const userId = activeUserId || getSessionUserId() || (await ensureSessionUserId(getSessionAuth()))
      if (!userId) {
        throw new Error('Unable to resolve the current user. Please sign in again and retry.')
      }

      const resp = await fetch(`${getCrmServiceUrl()}/catalog/${encodeURIComponent(userId)}/raw`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      })
      if (!resp.ok) {
        throw new Error('Unable to load the raw catalog PDF.')
      }

      const blob = await resp.blob()
      const rawCatalogUrl = URL.createObjectURL(blob)
      if (rawWindow) {
        rawWindow.location.href = rawCatalogUrl
      } else {
        window.open(rawCatalogUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      rawWindow?.close()
      console.error('Failed to open raw catalog', err)
      setStatusError(err instanceof Error ? err.message : 'Unable to load the raw catalog PDF.')
    } finally {
      setIsOpeningRawCatalog(false)
    }
  }

  const progressPercent = getCatalogProgress(catalogRecord?.status)
  const hasFailed = isCatalogFailed(catalogRecord)
  const hasCompleted = Boolean(catalogRecord) && isCatalogDone(catalogRecord) && !hasFailed
  const isProcessing = Boolean(catalogRecord) && !isCatalogDone(catalogRecord)
  const hasNumericProgress = hasNumericCatalogProgress(catalogRecord?.status)
  const progressLabel = hasFailed
    ? 'Failed'
    : hasCompleted
      ? 'Complete'
      : hasNumericProgress
        ? `${progressPercent}%`
        : formatCatalogStatus(catalogRecord?.status)
  const visualProgressPercent = hasFailed || hasCompleted
    ? 100
    : hasNumericProgress
      ? progressPercent
      : 12
  const connectDisabled = isUploading || isProcessing || !file
  const hasStartedExtraction = isUploading || isProcessing

  const showProgressCard = isUploading || isProcessing || hasFailed || hasCompleted
  const rawCatalogLabel = rawCatalogMetadata?.title || 'Raw catalog'

  return (
    <>
      <ContentHeader title="Catalog Connector"/>

      <div className="content-panel">
        <div className="card catalog-uploader-card">
          <div className="catalog-uploader-icon-wrapper">
            <FiPackage />
          </div>
          
          <h2 className="catalog-uploader-title">Connect your Catalog</h2>
          <p className="muted catalog-uploader-subtitle">
            {hasStartedExtraction
              ? 'Catalog extraction is in progress. This view will stay focused on the live progress until it finishes. You can leave at any time :) this will finish up in the background!'
              : 'Upload a PDF catalog and Bev will send it to the backend for extraction, then keep tracking progress in the background.'}
          </p>

          {!hasStartedExtraction ? (
            <>
              <div 
                className={`catalog-uploader-dropzone ${dragActive ? 'is-drag-active' : ''}`}
                onClick={() => document.getElementById('catalog-upload')?.click()}
                onDragEnter={handleDropzoneDrag}
                onDragOver={handleDropzoneDrag}
                onDragLeave={handleDropzoneDrag}
                onDrop={handleDropzoneDrop}
              >
                <input 
                  id="catalog-upload"
                  aria-label="Catalog PDF"
                  type="file" 
                  style={{ display: 'none' }} 
                  onChange={handleFileChange}
                  accept=".pdf,application/pdf"
                />
                <FiUpload className="catalog-uploader-dropzone-icon" />
                <div className="catalog-uploader-file-name">
                  {file ? file.name : 'Click to select or drag your catalog here'}
                </div>
                <div className="catalog-uploader-file-types">
                  PDF only
                </div>
              </div>

              <button type="button" 
                className="primary-button catalog-uploader-connect-button"
                disabled={connectDisabled}
                onClick={handleConnect}
              >
                {isUploading || isProcessing ? <Loader /> : 'Catalog Connect'}
              </button>
              {statusError ? (
                <div className="catalog-uploader-inline-error" aria-live="polite">
                  {statusError}
                </div>
              ) : null}
            </>
          ) : null}

          {rawCatalogMetadata ? (
            <div className="catalog-uploader-raw-section">
              <button
                type="button"
                className="ghost-button catalog-uploader-raw-title-button"
                disabled={isOpeningRawCatalog}
                onClick={handleOpenRawCatalog}
              >
                {isOpeningRawCatalog ? 'Opening raw catalog...' : rawCatalogLabel}
              </button>
              <div className="catalog-uploader-raw-meta">
                {rawCatalogMetadata.date_updated
                  ? `Updated ${new Date(rawCatalogMetadata.date_updated).toLocaleString()}`
                  : rawCatalogMetadata.status_message || 'Open raw catalog'}
              </div>
            </div>
          ) : null}

          {showProgressCard ? (
            <div className="catalog-uploader-status" aria-live="polite">
              <div className="catalog-uploader-status-row">
                <span className="catalog-uploader-status-label">Progress</span>
                <span
                  className={`catalog-uploader-status-pill ${
                    hasFailed
                      ? 'status-failed'
                      : progressPercent >= 100
                        ? 'status-completed'
                        : 'status-processing'
                  }`}
                >
                  {progressLabel}
                </span>
              </div>
              <div
                className="catalog-uploader-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={visualProgressPercent}
                aria-valuetext={progressLabel}
                aria-label="Catalog extraction progress"
              >
                <div
                  className={`catalog-uploader-progress-fill ${
                    hasFailed ? 'is-failed' : progressPercent >= 100 ? 'is-complete' : ''
                  }`}
                  style={{ width: `${visualProgressPercent}%` }}
                />
              </div>

              {catalogRecord?.status_message || hasFailed ? (
                <div className="catalog-uploader-status-text">
                  {catalogRecord?.status_message || 'Catalog extraction failed.'}
                </div>
              ) : null}
              {parsedSummary ? (
                <div className="catalog-uploader-status-text">{parsedSummary}</div>
              ) : null}
              {statusError ? (
                <div className="catalog-uploader-status-text catalog-uploader-status-error">
                  {statusError}
                </div>
              ) : null}
              {!isCatalogDone(catalogRecord) ? (
                <button
                  type="button"
                  className="secondary-button catalog-uploader-cancel-button"
                  style={{ marginTop: '1.2rem', width: '100%' }}
                  onClick={() => {
                    resetToBaseCatalogView()
                  }}
                >
                  Cancel Extraction
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default CatalogUploaderView
